import type {
  KanbanTaskId,
  TenantId,
} from '../../core/contracts/channel-agent.js';

export interface KanbanTask {
  id: KanbanTaskId;
  tenantId: TenantId;
  boardId: string;
  title: string;
  description: string;
  status: 'backlog' | 'in_progress' | 'blocked' | 'review' | 'done';
  assigneeId?: string;
  blockerRef?: string;
  comments: Array<{ author: string; text: string; timestamp: number }>;
  linkedTaskIds: KanbanTaskId[];
  createdAt: number;
  updatedAt: number;
  isPendingSync?: boolean;
  version?: number;
}

export interface KanbanBoard {
  id: string;
  tenantId: TenantId;
  name: string;
  revision: string;
  tasks: KanbanTask[];
}

export interface KanbanProvider {
  listBoards(tenantId: TenantId): Promise<KanbanBoard[]>;
  getBoardRevision(boardId: string): Promise<string>;
  listTasks(tenantId: TenantId, boardId?: string, limit?: number, offset?: number): Promise<KanbanTask[]>;
  createTask(params: {
    tenantId: TenantId;
    boardId: string;
    title: string;
    description: string;
    status?: KanbanTask['status'];
    assigneeId?: string;
    blockerRef?: string;
  }): Promise<KanbanTask>;
  updateTask(
    taskId: KanbanTaskId,
    patch: Partial<Pick<KanbanTask, 'title' | 'description' | 'status' | 'assigneeId' | 'blockerRef'>>,
    expectedVersion?: number
  ): Promise<KanbanTask>;
  addComment(taskId: KanbanTaskId, author: string, text: string): Promise<void>;
  linkTasks(taskIdA: KanbanTaskId, taskIdB: KanbanTaskId): Promise<void>;
}

export class InMemoryKanbanProvider implements KanbanProvider {
  private boards = new Map<string, KanbanBoard>();
  private revisionCounter = 1;

  constructor(tenantId?: TenantId, initialBoards: KanbanBoard[] = []) {
    for (const board of initialBoards) {
      this.boards.set(board.id, board);
    }
    // If tenantId provided and in demo mode with no initial boards, optionally seed a demo board
    if (tenantId && initialBoards.length === 0 && import.meta.env.VITE_DATA_MODE === 'demo') {
      const boardId = `board-scrum-${tenantId}`;
      this.boards.set(boardId, {
        id: boardId,
        tenantId,
        name: 'Sprint 42 Kanban Board',
        revision: `rev-${this.revisionCounter}`,
        tasks: [],
      });
    }
  }

  async listBoards(tenantId: TenantId): Promise<KanbanBoard[]> {
    return Array.from(this.boards.values()).filter((b) => b.tenantId === tenantId);
  }

  async getBoardRevision(boardId: string): Promise<string> {
    const board = this.boards.get(boardId);
    return board ? board.revision : `rev-${this.revisionCounter}`;
  }

  async listTasks(tenantId: TenantId, boardId?: string, limit?: number, offset?: number): Promise<KanbanTask[]> {
    const boards = await this.listBoards(tenantId);
    let tasks: KanbanTask[] = [];
    for (const b of boards) {
      if (!boardId || b.id === boardId) {
        tasks = tasks.concat(b.tasks);
      }
    }
    
    // Sort tasks by updatedAt desc to ensure windowing works predictably
    tasks.sort((a, b) => b.updatedAt - a.updatedAt);
    
    if (offset !== undefined) {
      tasks = tasks.slice(offset);
    }
    if (limit !== undefined) {
      tasks = tasks.slice(0, limit);
    }
    
    return tasks;
  }

  async createTask(params: {
    tenantId: TenantId;
    boardId: string;
    title: string;
    description: string;
    status?: KanbanTask['status'];
    assigneeId?: string;
    blockerRef?: string;
  }): Promise<KanbanTask> {
    let board = this.boards.get(params.boardId);
    if (!board) {
      this.revisionCounter++;
      board = {
        id: params.boardId,
        tenantId: params.tenantId,
        name: `Board ${params.boardId}`,
        revision: `rev-${this.revisionCounter}`,
        tasks: [],
      };
      this.boards.set(params.boardId, board);
    }

    const newTask: KanbanTask = {
      id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 6)}` as KanbanTaskId,
      tenantId: params.tenantId,
      boardId: params.boardId,
      title: params.title,
      description: params.description,
      status: params.status || 'backlog',
      assigneeId: params.assigneeId,
      blockerRef: params.blockerRef,
      comments: [],
      linkedTaskIds: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      version: 1,
    };

    board.tasks.push(newTask);
    this.revisionCounter++;
    board.revision = `rev-${this.revisionCounter}`;
    return newTask;
  }

  async updateTask(
    taskId: KanbanTaskId,
    patch: Partial<Pick<KanbanTask, 'title' | 'description' | 'status' | 'assigneeId' | 'blockerRef'>>,
    expectedVersion?: number
  ): Promise<KanbanTask> {
    for (const board of this.boards.values()) {
      const task = board.tasks.find((t) => t.id === taskId);
      if (task) {
        if (expectedVersion !== undefined && task.version !== expectedVersion) {
          throw new Error(`Conflict: Task ${taskId} has been modified by another peer (expected version ${expectedVersion}, got ${task.version || 1})`);
        }
        Object.assign(task, patch, { 
          updatedAt: Date.now(),
          version: (task.version || 1) + 1
        });
        this.revisionCounter++;
        board.revision = `rev-${this.revisionCounter}`;
        return task;
      }
    }
    throw new Error(`Task ${taskId} not found`);
  }

  async addComment(taskId: KanbanTaskId, author: string, text: string): Promise<void> {
    for (const board of this.boards.values()) {
      const task = board.tasks.find((t) => t.id === taskId);
      if (task) {
        task.comments.push({ author, text, timestamp: Date.now() });
        task.updatedAt = Date.now();
        this.revisionCounter++;
        board.revision = `rev-${this.revisionCounter}`;
        return;
      }
    }
  }

  async linkTasks(taskIdA: KanbanTaskId, taskIdB: KanbanTaskId): Promise<void> {
    let foundA: KanbanTask | undefined;
    let foundB: KanbanTask | undefined;

    for (const board of this.boards.values()) {
      if (!foundA) foundA = board.tasks.find((t) => t.id === taskIdA);
      if (!foundB) foundB = board.tasks.find((t) => t.id === taskIdB);
    }

    if (foundA && foundB) {
      if (!foundA.linkedTaskIds.includes(taskIdB)) foundA.linkedTaskIds.push(taskIdB);
      if (!foundB.linkedTaskIds.includes(taskIdA)) foundB.linkedTaskIds.push(taskIdA);
    }
  }
}
